import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Alert,
  Avatar,
  Box,
  Checkbox,
  Chip,
  Paper,
  Skeleton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
  Typography,
  useTheme
} from "@mui/material";
import { useEffect, useState } from "react";
import apiService from "../../../service/Api";
import { tokens } from "../../../theme";

export type HandoffStatus = "delivered" | "pending" | "unassigned";

export interface HandoffRowData {
  id: string;
  streamName: string;
  avatarUrl: string | null;
  copied: boolean;
  handedOver: boolean;
  status: HandoffStatus;
}

interface StreamApiRow {
  id: number;
  title: string;
  booking?: {
    deceased?: Array<{ person_image?: string | null }>;
  };
  is_recording_copied: boolean | null;
  is_recording_handed_over: boolean | null;
  is_recording_delivered: string;
}

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

function mapStreamToRow(s: StreamApiRow): HandoffRowData {
  return {
    id: String(s.id),
    streamName: s.title,
    avatarUrl: s.booking?.deceased?.[0]?.person_image ?? null,
    copied: Boolean(s.is_recording_copied),
    handedOver: Boolean(s.is_recording_handed_over),
    status: (s.is_recording_delivered?.toLowerCase() as HandoffStatus) ?? "pending",
  };
}

interface RecordingsHandoffTableProps {
  sx?: SxProps<Theme>;
}

export default function RecordingsHandoffTable({ sx }: RecordingsHandoffTableProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [rows, setRows] = useState<HandoffRowData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const pageSize = 8;

  useEffect(() => {
    setLoading(true);
    apiService
      .get<PaginatedResponse<StreamApiRow>>(
        `/api/streams/stream/?ordering=-created_at&page=${page}&page_size=${pageSize}`,
      )
      .then((res) => {
        setRows(res.data.results.map(mapStreamToRow));
        setCount(res.data.count);
      })
      .catch(() => setError("Failed to load recordings handoff data"))
      .finally(() => setLoading(false));
  }, [page]);

  const handleToggle = async (id: string, field: "copied" | "handedOver") => {
    const apiField = field === "copied" ? "is_recording_copied" : "is_recording_handed_over";
    const prevRows = rows;
    const target = rows.find((r) => r.id === id);
    if (!target) return;

    const newValue = !target[field];

    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: newValue } : row)),
    );
    setSavingIds((prev) => new Set(prev).add(id));

    try {
      const res = await apiService.patch<StreamApiRow>(
        `/api/streams/stream/${id}/`,
        { [apiField]: newValue },
      );
      setRows((prev) =>
        prev.map((row) => (row.id === id ? mapStreamToRow(res.data) : row)),
      );
    } catch {
      setRows(prevRows);
      setError("Failed to update recording status");
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const getStatusChip = (status: HandoffStatus) => {
    switch (status) {
      case "delivered":
        return (
          <Chip
            label="Delivered"
            size="small"
            sx={{
              backgroundColor: "#e8f7f0",
              color: "#10b981",
              fontWeight: 700,
              borderRadius: "16px",
            }}
          />
        );
      case "pending":
        return (
          <Chip
            label="Pending"
            size="small"
            sx={{
              backgroundColor: "#fff8ec",
              color: "#f59e0b",
              fontWeight: 700,
              borderRadius: "16px",
            }}
          />
        );
      default:
        return (
          <Typography variant="body2" sx={{ color: colors.primary[400], pl: 2 }}>
            -
          </Typography>
        );
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "24px",
        backgroundColor: colors.primary[900],
        width: "100%",
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      <Typography
        variant="h5"
        fontWeight={700}
        color={colors.primary[100]}
        sx={{ mb: 2 }}
      >
        Recordings & Handoff
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ position: "relative", width: "100%" }}>
        <TableContainer>
          <Table sx={{ minWidth: 400 }} aria-label="handoff tracking dashboard table">
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    borderBottom: `1px solid ${colors.primary[800]}`,
                    pb: 1.5,
                    px: 1,
                  },
                }}
              >
                <TableCell>
                  <Typography variant="caption" fontWeight={600} color={colors.primary[400]}>
                    Stream Name
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="caption" fontWeight={600} color={colors.primary[400]}>
                    Copied
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="caption" fontWeight={600} color={colors.primary[400]}>
                    Handed Over
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="caption" fontWeight={600} color={colors.primary[400]}>
                    Status
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: pageSize }).map((_, idx) => (
                  <TableRow
                    key={`skeleton-${idx}`}
                    sx={{
                      "& td": {
                        borderBottom: `1px solid ${colors.primary[800]}10`,
                        py: 1.5,
                        px: 1,
                      },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Skeleton variant="circular" width={32} height={32} animation="wave" />
                        <Skeleton variant="text" width="65%" height={20} animation="wave" />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center">
                        <Skeleton variant="rectangular" width={18} height={ 18} sx={{ borderRadius: "4px" }} animation="wave" />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center">
                        <Skeleton variant="rectangular" width={18} height={18} sx={{ borderRadius: "4px" }} animation="wave" />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center">
                        <Skeleton variant="rectangular" width={70} height = {24} sx={{ borderRadius: "8px" }} animation="wave" />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      "& td": {
                        borderBottom: `1px solid ${colors.primary[800]}10`,
                        py: 1.5,
                        px: 1,
                      },
                      opacity: savingIds.has(row.id) ? 0.6 : 1,
                      transition: "opacity 0.2s ease",
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar src={row.avatarUrl ?? undefined} sx={{ width: 32, height: 32 }} />
                        <Typography variant="body2" fontWeight={600} color={colors.primary[100]}>
                          {row.streamName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={row.copied}
                        onChange={() => handleToggle(row.id, "copied")}
                        disabled={savingIds.has(row.id)}
                        disableRipple
                        checkedIcon={<CheckBoxIcon sx={{ color: "#10b981" }} />}
                        icon={<CheckBoxOutlineBlankIcon sx={{ color: `${colors.primary[400]}50` }} />}
                        sx={{ p: 0 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={row.handedOver}
                        onChange={() => handleToggle(row.id, "handedOver")}
                        disabled={savingIds.has(row.id)}
                        disableRipple
                        checkedIcon={<CheckBoxIcon sx={{ color: "#10b981" }} />}
                        icon={<CheckBoxOutlineBlankIcon sx={{ color: `${colors.primary[400]}50` }} />}
                        sx={{ p: 0 }}
                      />
                    </TableCell>
                    <TableCell align="center">{getStatusChip(row.status)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <TablePagination
        component="div"
        count={count}
        page={page - 1}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[pageSize]}
        onPageChange={(_, newPage) => setPage(newPage + 1)}
        sx={{ mt: "auto", borderTop: `1px solid ${colors.primary[800]}20` }}
      />
    </Paper>
  );
}